/* Small presentational pieces of the redesigned admin console. */
import React from 'react';
import PropTypes from 'prop-types';
import {
    initials, ROLE_LABEL, statusLabel, USER_KIND_LABEL,
} from '../../utils/adminConsole';
import { accessChipText } from '../../utils/adminAccess';
import { DEFAULT_DATABASE } from '../AdminConsole/DataSourceFields';
import {
    hasFailingStep, hasWarningStep, humanizeStep, TENANT_STATUS_HINT, TENANT_STATUS_LABEL,
} from '../AdminConsole/DataSourceCheck';
import {
    CheckIcon, InfoIcon, LockIcon, PencilIcon, WarnIcon,
} from './icons';

export function SectionHead({
    eyebrow, title, children, actions, titleRef,
}) {
    return (
        <div className="ad2-sec-head">
            <div className="ad2-sec-t">
                {eyebrow && <span className="v2-eyebrow">{eyebrow}</span>}
                <h1 className="ad2-title" ref={titleRef} tabIndex={titleRef ? -1 : undefined}>{title}</h1>
                {children && <p className="ad2-sub">{children}</p>}
            </div>
            {actions && <div className="ad2-sec-acts">{actions}</div>}
        </div>
    );
}
SectionHead.propTypes = {
    eyebrow: PropTypes.node, title: PropTypes.node.isRequired, children: PropTypes.node, actions: PropTypes.node, titleRef: PropTypes.object,
};

/** A tinted note: info (default), warn, bad, ok. */
export function Note({ tone = 'info', children, role }) {
    const Icon = tone === 'ok' ? CheckIcon : tone === 'info' ? InfoIcon : WarnIcon;
    return (
        <div className={`ad2-note ad2-note--${tone}`} role={role}>
            <Icon />
            <span>{children}</span>
        </div>
    );
}
Note.propTypes = { tone: PropTypes.oneOf(['info', 'warn', 'bad', 'ok']), children: PropTypes.node, role: PropTypes.string };

/** The console-level error banner (today's red banner). */
export function Alert({ children }) {
    if (!children) return null;
    return <div className="ad2-alert" role="alert">{children}</div>;
}
Alert.propTypes = { children: PropTypes.node };

export function Avatar({ user, kind, large = false }) {
    return (
        <span className={`ad2-ua ad2-ua--${kind === 'staff' ? 'staff' : 'dealer'}${large ? ' ad2-ua--lg' : ''}`} aria-hidden="true">
            {initials(user)}
        </span>
    );
}
Avatar.propTypes = { user: PropTypes.object.isRequired, kind: PropTypes.string, large: PropTypes.bool };

export function KindChip({ kind }) {
    return <span className={`ad2-kind ad2-kind--${kind}`}>{USER_KIND_LABEL[kind]}</span>;
}
KindChip.propTypes = { kind: PropTypes.oneOf(['staff', 'holding', 'dealer']).isRequired };

export function OrgTypeChip({ type, label }) {
    const kind = type === 'VENDOR' ? 'staff' : type === 'TOG' ? 'holding' : 'dealer';
    return <span className={`ad2-kind ad2-kind--${kind}`}>{label}</span>;
}
OrgTypeChip.propTypes = { type: PropTypes.string, label: PropTypes.node };

export function RoleText({ role }) {
    const admin = role === 'dealer_admin' || role === 'super_admin';
    return <span className={`ad2-role${admin ? ' ad2-role--admin' : ''}`}>{ROLE_LABEL[role] || role || '—'}</span>;
}
RoleText.propTypes = { role: PropTypes.string };

export function StatusChip({ status }) {
    const tone = status === 'active' ? 'active' : status === 'suspended' ? 'suspended' : 'other';
    return <span className={`ad2-st ad2-st--${tone}`}><i aria-hidden="true" />{statusLabel(status)}</span>;
}
StatusChip.propTypes = { status: PropTypes.string };

/** The IronSight access switch (today's toggle: one PATCH of the full app list). */
export function AppSwitch({ on, onToggle, disabled, title, label }) {
    return (
        <button
            type="button"
            className="ad2-sw"
            aria-pressed={Boolean(on)}
            aria-label={label}
            title={title}
            disabled={disabled}
            onClick={onToggle}
        >
            <span className="ad2-sw-tr" aria-hidden="true" />
            {on ? 'On' : 'Off'}
        </button>
    );
}
AppSwitch.propTypes = {
    on: PropTypes.bool, onToggle: PropTypes.func.isRequired, disabled: PropTypes.bool, title: PropTypes.string, label: PropTypes.string.isRequired,
};

/** A1 chip: Edit / Read-only / No access (expected, derived). */
export function AccessChip({ summary }) {
    const Icon = summary.level === 'edit' ? PencilIcon : summary.level === 'ro' ? LockIcon : null;
    const title = summary.level === 'none'
        ? summary.why
        : summary.level === 'ro' ? 'Can open these dealerships but cannot create tasks, notes or other changes' : 'Can create tasks and changes there';
    return (
        <span className={`ad2-acc ad2-acc--${summary.level}`} title={title}>
            {Icon && <Icon />}
            {accessChipText(summary)}
            {summary.level === 'none' && summary.why && <span className="ad2-acc-w">· {summary.why}</span>}
        </span>
    );
}
AccessChip.propTypes = { summary: PropTypes.object.isRequired };

export function AccessCell({ level }) {
    if (level === 'edit') return <span className="ad2-acc ad2-acc--edit"><PencilIcon />Edit</span>;
    if (level === 'ro') return <span className="ad2-acc ad2-acc--ro"><LockIcon />Read-only</span>;
    return <span className="ad2-acc ad2-acc--none">None</span>;
}
AccessCell.propTypes = { level: PropTypes.string };

/** A data source's status (today's labels and hints), or "Not connected". */
export function SourceChip({ tenant }) {
    if (!tenant) return <span className="ad2-ds ad2-ds--none">Not connected</span>;
    return (
        <span className="ad2-ds-wrap">
            <span className={`ad2-ds ad2-ds--${tenant.status}`} title={TENANT_STATUS_HINT[tenant.status] || ''}>
                <i aria-hidden="true" />
                {TENANT_STATUS_LABEL[tenant.status] || tenant.status}
            </span>
            {tenant.is_demo && <span className="ad2-kind ad2-kind--demo">Demo</span>}
        </span>
    );
}
SourceChip.propTypes = { tenant: PropTypes.object };

export function Spinner() {
    return <span className="ad2-spin" aria-hidden="true" />;
}

/** "Querying the dealer database…" while a check or activation runs. */
export function CheckPending() {
    return (
        <span className="ad2-pending" role="status">
            <Spinner />
            Querying the dealer database — this can take a few seconds.
        </span>
    );
}

/** Per-step result of a connection check, with today's verdict copy. */
export function CheckResult({ result }) {
    if (!result) return null;
    const steps = result.steps || [];
    const failed = hasFailingStep(steps);
    const warned = hasWarningStep(steps);
    return (
        <div className="ad2-check">
            <ul className="ad2-steps">
                {steps.map((s, i) => (
                    <li key={`${s.step}-${i}`} className={`ad2-step ad2-step--${s.status}`}>
                        <i aria-hidden="true" />
                        <span>
                            {humanizeStep(s.step)}
                            {s.detail && <span className="ad2-step-d">{s.detail}</span>}
                        </span>
                        <span className="ad2-step-tg">{s.status === 'ok' ? 'OK' : s.status === 'warn' ? 'Partial' : 'Failed'}</span>
                    </li>
                ))}
                {steps.length === 0 && <li className="ad2-muted">The check returned no steps.</li>}
            </ul>
            {failed && (
                <Note tone="bad">
                    This source cannot be activated until the failing checks above are resolved.
                    Send the detail text back to the data team — they own the source.
                </Note>
            )}
            {!failed && warned && (
                <Note tone="warn">
                    Good enough to activate. The amber items are datasets we could not read,
                    so the panels that depend on them will stay empty until the data lands.
                </Note>
            )}
            {!failed && !warned && steps.length > 0 && (
                <Note tone="ok">Everything we need is readable. This source is ready to activate.</Note>
            )}
        </div>
    );
}
CheckResult.propTypes = { result: PropTypes.shape({ steps: PropTypes.array }) };

/** The endpoint, database and lake of a source (today's text). */
export function SourceEndpoint({ tenant }) {
    return (
        <div className="ad2-ep-cell">
            <div className="ad2-ep">{tenant.synapse_server || '—'}</div>
            <div className="ad2-ep-sub">
                {tenant.database || DEFAULT_DATABASE}
                {tenant.lake_account ? ` · ${tenant.lake_account}` : ' · no storage account'}
            </div>
        </div>
    );
}
SourceEndpoint.propTypes = { tenant: PropTypes.object.isRequired };

/** The coordinates of a dealer's warehouse (today's DataSourceFields, v2 skin). */
export function SourceFields({ value, onChange, disabled, idPrefix }) {
    const set = (patch) => onChange({ ...value, ...patch });
    return (
        <>
            <label className="ad2-fld" htmlFor={`${idPrefix}-srv`}>
                <span>Synapse endpoint</span>
                <input
                    id={`${idPrefix}-srv`}
                    className="v2-input"
                    type="text"
                    disabled={disabled}
                    placeholder="dealer-syn-ondemand.sql.azuresynapse.net"
                    value={value.synapse_server}
                    onChange={(e) => set({ synapse_server: e.target.value })}
                />
            </label>
            <div className="ad2-two">
                <label className="ad2-fld" htmlFor={`${idPrefix}-lake`}>
                    <span>Storage account <span className="ad2-muted">optional</span></span>
                    <input
                        id={`${idPrefix}-lake`}
                        className="v2-input"
                        type="text"
                        disabled={disabled}
                        placeholder="stdealerlake"
                        value={value.lake_account}
                        onChange={(e) => set({ lake_account: e.target.value })}
                    />
                </label>
                <label className="ad2-fld" htmlFor={`${idPrefix}-db`}>
                    <span>Database</span>
                    <input
                        id={`${idPrefix}-db`}
                        className="v2-input"
                        type="text"
                        disabled={disabled}
                        placeholder={DEFAULT_DATABASE}
                        value={value.database}
                        onChange={(e) => set({ database: e.target.value })}
                    />
                </label>
            </div>
            <label className="ad2-chk">
                <input type="checkbox" disabled={disabled} checked={!!value.is_demo} onChange={(e) => set({ is_demo: e.target.checked })} />
                <span>Demo source — sample data, not this dealer&apos;s live warehouse</span>
            </label>
            <p className="ad2-hint">
                The data team hands you these when the dealer&apos;s warehouse is registered.
                We only read from it — connecting here builds nothing.
            </p>
        </>
    );
}
SourceFields.propTypes = {
    value: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool, idPrefix: PropTypes.string.isRequired,
};

/** A /login/ or tenant/ prefixed slug input. */
export function PrefixInput({ id, prefix, value, onChange, disabled, label }) {
    return (
        <div className="ad2-pfx">
            <span aria-hidden="true">{prefix}</span>
            <input id={id} type="text" value={value} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.value)} />
        </div>
    );
}
PrefixInput.propTypes = {
    id: PropTypes.string, prefix: PropTypes.string.isRequired, value: PropTypes.string, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool, label: PropTypes.string,
};
